import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Pause, Radio, Zap } from "lucide-react";
import { getLiveNow, type AdminLiveRow, type OversightMode } from "../../api/admin";
import AdminStopKillDialog, {
  type AdminAction,
} from "../../components/AdminStopKillDialog";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Chip from "../../components/Chip";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatusPill from "../../components/StatusPill";
import ToggleGroup from "../../components/ToggleGroup";
import { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR, signedClass } from "../../lib/format";

/** Every running deployment across all users — the one place to look (and
 *  act) when the market moves hard, instead of opening each user in turn. */
export default function AdminLiveNowPage() {
  const { user: me } = useAuth();
  const [mode, setMode] = useState<OversightMode | undefined>(undefined);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-live", mode ?? "all"],
    queryFn: () => getLiveNow(mode),
    refetchInterval: 3_000,
  });
  const [action, setAction] = useState<{ row: AdminLiveRow; action: AdminAction } | null>(
    null,
  );

  const rows = data ?? [];
  const totalToday = rows.reduce((a, r) => a + Number(r.day_realized_pnl), 0);
  const totalUnreal = rows.reduce((a, r) => a + Number(r.unrealized_pnl), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Live now</h1>
          <p className="text-sm text-muted mt-0.5">
            Running deployments across all users. Refreshes every 3 seconds.
          </p>
        </div>
        <ToggleGroup
          value={mode ?? ""}
          onChange={(v) => setMode(v === "paper" || v === "live" ? v : undefined)}
          options={[
            { value: "", label: "All" },
            { value: "paper", label: "Paper" },
            { value: "live", label: "Live" },
          ]}
        />
      </div>

      {isError && (
        <div className="text-sm text-down-600 dark:text-down-400">{apiErrorMessage(error)}</div>
      )}

      <Card
        title={`${rows.length} running`}
        subtitle={
          rows.length > 0 ? (
            <span>
              Realized today{" "}
              <span className={signedClass(totalToday)}>
                {formatINR(totalToday, { sign: true })}
              </span>{" "}
              · unrealized{" "}
              <span className={signedClass(totalUnreal)}>
                {formatINR(totalUnreal, { sign: true })}
              </span>
            </span>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="text-sm text-muted">Loading…</div>
        ) : (
          <DataTable
            rows={rows}
            rowKey={(r) => r.deployment_id}
            empty={
              <EmptyState
                icon={<Radio size={32} />}
                title="Nothing running"
                message="Running deployments of every user show up here."
              />
            }
            columns={[
              {
                key: "owner",
                header: "User",
                render: (r) => (
                  <Link
                    to={`/admin/users/${r.owner_id}`}
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {r.owner_name || r.owner_email}
                  </Link>
                ),
              },
              {
                key: "name",
                header: "Deployment",
                render: (r) => (
                  <Link to={`/deployments/${r.deployment_id}`} className="hover:underline">
                    {r.name}
                  </Link>
                ),
              },
              {
                key: "strategy",
                header: "Strategy",
                mono: true,
                render: (r) => r.strategy_key,
              },
              {
                key: "mode",
                header: "Mode",
                render: (r) => (
                  <Chip tone={r.mode === "live" ? "warn" : "info"} mono>
                    {r.mode}
                  </Chip>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (r) => <StatusPill status={r.status} />,
              },
              {
                key: "feed",
                header: "Feed",
                render: (r) =>
                  r.feed_alert ? (
                    <span className="text-down-600 dark:text-down-400" title={r.feed_alert}>
                      stale
                    </span>
                  ) : r.seconds_since_last_tick == null ? (
                    <span className="text-muted">no ticks</span>
                  ) : (
                    <span className="text-muted">
                      {Math.round(r.seconds_since_last_tick)}s
                    </span>
                  ),
              },
              {
                key: "today",
                header: "Realized today",
                align: "right",
                mono: true,
                render: (r) => (
                  <span className={signedClass(r.day_realized_pnl)}>
                    {formatINR(r.day_realized_pnl, { sign: true })}
                  </span>
                ),
              },
              {
                key: "unrealized",
                header: "Unrealized",
                align: "right",
                mono: true,
                render: (r) => (
                  <span className={signedClass(r.unrealized_pnl)}>
                    {formatINR(r.unrealized_pnl, { sign: true })}
                  </span>
                ),
              },
              {
                key: "open",
                header: "Open pos.",
                align: "right",
                render: (r) => r.open_positions,
              },
              {
                key: "actions",
                header: "",
                align: "right",
                render: (r) =>
                  // Your own deployments are managed from their own page, with
                  // the ordinary owner controls.
                  r.owner_id === me?.id ? (
                    <Link
                      to={`/deployments/${r.deployment_id}`}
                      className="text-xs text-muted hover:underline"
                    >
                      yours
                    </Link>
                  ) : (
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="secondary"
                        iconLeft={<Pause size={12} />}
                        onClick={() => setAction({ row: r, action: "stop" })}
                      >
                        Stop
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        iconLeft={<Zap size={12} />}
                        onClick={() => setAction({ row: r, action: "kill" })}
                      >
                        Kill
                      </Button>
                    </div>
                  ),
              },
            ]}
          />
        )}
      </Card>

      {action && (
        <AdminStopKillDialog
          target={{
            id: action.row.deployment_id,
            name: action.row.name,
            mode: action.row.mode,
            ownerLabel: action.row.owner_name || action.row.owner_email,
          }}
          action={action.action}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
